import {
    Component, Input, OnDestroy, OnChanges, AfterViewInit, ViewChild, SimpleChanges, OnInit, ChangeDetectorRef
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MessageService } from 'base';
import { DiscResultsService } from 'discovery';
import { Device } from 'DiscoveryEngine.TS/Core';
import { List, ValueEncoder } from 'DiscoveryEngine.TS/Utils';
import { IdentityService } from 'identity';
import {
    GetDeviceResultDto, IGetPortStateResultDto, IPortStateDto_Get, ISetPortStateResultDto, PortStateDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { IfEntry, IfTable } from 'DiscoveryEngine.TS/InfoSets/IfTable';
import { SnmpConstants } from 'DiscoveryEngine.TS/Core/SnmpConstants';
import { PortStateType } from 'UvExplorerServer.Dtos.TS/Session';
import { NavigationService } from 'netview';
import { UserPermissionsService } from 'identity';
import { ToolsService } from 'tools';

@Component({
    selector: 'single-port-enable-disable-editor',
    templateUrl: './single-port-enable-disable-editor.component.html',
    styleUrls: ['./single-port-enable-disable-editor.component.scss']
})
export class SinglePortEnableDisableEditorComponent implements OnChanges, OnDestroy, OnInit {

    @Input()
    public deviceGuid: string;

    @Input()
    public ifIndex: number;

    @Input()
    public visible: boolean;

    public ifName: string;

    public form: UntypedFormGroup;
    public adminStatus: UntypedFormControl;

    private device: Device;
    private interface: IfEntry;

    constructor(private formBuilder: UntypedFormBuilder,
        private navService: NavigationService,
        private messageService: MessageService,
        private toolsService: ToolsService,
        private discResultsService: DiscResultsService,
        private identityService: IdentityService,
        private userPermissionsService: UserPermissionsService,
        private changeDetector: ChangeDetectorRef) {

        this.deviceGuid = "";
        this.ifIndex = -1;
        this.visible = false;
        this.ifName = "";
        this.form = null;
        this.adminStatus = null;
        this.device = null;
        this.interface = null;
    }

    public ngOnChanges(changes: SimpleChanges): void {

        if (!!changes["deviceGuid"] || !!changes["ifIndex"] || !!changes["visible"]) {
            if (!!this.deviceGuid && this.visible) {
                if (this.adminStatus) {
                    this.adminStatus.setValue("");
                }
                setTimeout(() => this.getDevice(), 0);
            }
            else {
                if (this.adminStatus) {
                    this.adminStatus.setValue("");
                }
                this.setDevice(null);
            }
        }
    }

    public ngOnInit(): void {
        this.rebuildForm();
    }

    public ngOnDestroy(): void {

    }

    private rebuildForm(): void {
        let adminStatus = "";

        if (this.interface) {
            adminStatus = (this.interface.IsOperStatusUp ? "Enable" : "Disable");
        }

        this.adminStatus = new UntypedFormControl(adminStatus);

        this.form = this.formBuilder.group({
            adminStatus: adminStatus,
        });
    }

    public get enableUpdate(): boolean {
        return (this.userPermissionsService.canEnableDisablePorts &&
            !!this.device && !!this.adminStatus.value && !!this.interface);
    }

    public get enableRefresh(): boolean {
        return (!!this.device && !!this.interface);
    }

    public refresh(): void {
        this.getDevicePortState();
    }

    public update(): void {
        if (!!this.device && !!this.adminStatus.value && !!this.interface) {

            let sessionGuid = this.identityService.sessionGuid;
            let netViewGuid = this.navService.location.netViewGuid;
            let agentGuid = this.navService.location.agentGuid;
            let schedDiscGuid = this.navService.location.schedDiscGuid;
            let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

            let portStateDto = this.createPortStateDto(this.device, this.interface);

            this.toolsService.setPortState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
                schedDiscRsltGuid, PortStateType.AdminStatus, [portStateDto], true).subscribe(
                    (result: ISetPortStateResultDto) => {
                        let succeeded = false;
                        if (result.entries) {
                            succeeded = result.entries.every((entry) => entry.succeeded);
                        }
                        if (succeeded) {
                            this.messageService.displayInfoMessage("Port was successfully configured");
                        }
                        else {
                            this.messageService.displayErrorMessage("Port could not be configured");
                        }
                    },
                    (error) => {
                        this.messageService.displayErrorMessage("Port could not be configured");
                    });
        }
    }

    private createPortStateDto(device: Device, ifEntry: IfEntry): PortStateDto {
        let result = new PortStateDto();

        result.DeviceGuid = ValueEncoder.EncodeGuid(device.GUID);
        result.IfIndex = ifEntry.IfIndex;
        result.AdminStatus = this.createAdminStatus(this.adminStatus.value);

        return result;
    }

    private createAdminStatus(value: string): number {
        switch (value.toLowerCase()) {
            case "enable":
                return SnmpConstants.ifStatus_up;
            case "disable":
                return SnmpConstants.ifStatus_down;
            default:
                return 0;
        }
    }

    private setDevice(device: Device): void {
        if (device) {
            this.device = device;

            this.interface = null;
            if (this.device.HasIfTable) {
                this.interface = this.device.IfTable.Find(this.ifIndex);
                if (this.interface) {
                    this.ifName = this.interface.DisplayName;
                }
            }
        }
        else {
            this.device = null;
            this.interface = null;
            this.ifName = "";
        }
    }

    private getDevice(): void {

        let sessionGuid = this.identityService.sessionGuid;
        let netViewGuid = this.navService.location.netViewGuid;
        let agentGuid = this.navService.location.agentGuid;
        let schedDiscGuid = this.navService.location.schedDiscGuid;
        let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

        let deviceGuids = new List<string>();
        deviceGuids.Add(this.deviceGuid);

        this.messageService.startWork();
        this.discResultsService.getDevice(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
            schedDiscRsltGuid, "network", deviceGuids, true).subscribe(
                (result: GetDeviceResultDto) => {
                    this.messageService.endWork();
                    if (result.HasDevices) {
                        this.setDevice(result.Devices.Get(0));
                        //this.getDevicePortState();
                        this.rebuildForm();
                        this.changeDetector.detectChanges();
                    }
                    else {
                        this.messageService.displayErrorMessage("Could not retrieve selected device");
                    }
                },
                (error?) => {
                    this.messageService.endWork();
                    this.messageService.displayErrorMessage("Could not retrieve selected device");
                });
    }

    private getDevicePortState(): void {

        if (!!this.device && this.device.HasIfTable) {
            let sessionGuid = this.identityService.sessionGuid;
            let netViewGuid = this.navService.location.netViewGuid;
            let agentGuid = this.navService.location.agentGuid;
            let schedDiscGuid = this.navService.location.schedDiscGuid;
            let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

            this.messageService.startWork();
            let ifIndexes = [this.ifIndex];
            this.toolsService.getPortState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
                schedDiscRsltGuid, [PortStateType.AdminStatus], [this.deviceGuid], ifIndexes, true).subscribe(
                    (result: IGetPortStateResultDto) => {
                        this.messageService.endWork();
                        if (!!this.device && this.device.HasIfTable) {
                            let errors = false;
                            let message = "";

                            if (!result.entries || result.entries.length == 0) {
                                errors = true;
                            }
                            else {
                                errors = result.entries.some((entry: IPortStateDto_Get) => {
                                    if (!entry.succeeded) {
                                        message = entry.message;
                                        return true;
                                    }
                                    else {
                                        return false;
                                    }
                                });
                            }

                            if (errors) {
                                this.messageService.displayErrorMessage("Could not retrieve device port state");
                                console.error(message);
                            }
                            else {
                                let ifTable: IfTable = this.device.IfTable;

                                result.entries.forEach((entry: IPortStateDto_Get) => {
                                    let ifEntry: IfEntry = ifTable.Find(entry.ifIndex);
                                    if (ifEntry) {
                                        ifEntry.AdminStatus = entry.adminStatus;
                                        ifEntry.OperStatus = entry.operStatus;
                                        ifEntry.PoeAdminEnable = entry.poeAdminEnable;
                                        ifEntry.PoeDetectionStatus = entry.poeDetectionStatus;
                                        ifEntry.ConnectedDevices = entry.connectedDevices;
                                    }
                                    else {
                                        console.warn(`Missing device interface: ${this.device.DisplayName} - ${entry.ifIndex}`);
                                    }
                                });

                                this.rebuildForm();
                                this.changeDetector.detectChanges();
                            }
                        }
                    },
                    (error?) => {
                        this.messageService.endWork();
                        this.messageService.displayErrorMessage("Could not retrieve device port state");
                    });
        }
    }

}
